import * as React from 'react';
import {useSelector} from '@xstate/react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {BrandButton} from '../../lib/brand-button/brand-button';
import {useBookingMachine} from '../../hooks/useBookingMachine';
import {
  selectSelectedTimeSlot,
  selectTotalDays,
  selectIsMultiDay,
  selectCanNavigateNext,
  selectIsSequentialTemplate,
} from '../../selectors/booking-selector';
import {RootMachineContext} from '../../machines/rootMachine';

import css from './booking-navigation-buttons.css';
import {TEST_ID} from '../../utils/testId';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa/qa';


export const BookingNavigationButtons = () => {
  const {t} = useLabelTranslation();
  const {bookingActor, bookingState} = useBookingMachine();
  const rootState = RootMachineContext.useSelector((state) => state);
  const canGoToeNextWeek = useSelector(bookingActor, selectCanNavigateNext);
  const selectedTimeSlot = useSelector(bookingActor, selectSelectedTimeSlot);
  const currentSequenceNumber = useSelector(
    bookingActor,
    (state) => state.context.currentSequenceNumber ?? 0,
  );
  const totalDays = useSelector(bookingActor, selectTotalDays);
  const isMultiDay = useSelector(bookingActor, selectIsMultiDay);
  const isSequentialTemplate = useSelector(
    bookingActor,
    selectIsSequentialTemplate,
  );

  const hasAvailabilityError = bookingState.matches({
    dateTimePicker: 'availabilityError',
  });

  const isAvailabilityLoading = bookingState.matches({
    dateTimePicker: 'availabilityLoading',
  });
  const isRescheduleFlow = rootState.matches('rescheduleFlow');

  const handleNextDay = () => {
    bookingActor.send({type: 'day.next'});
  };

  const handlePrevDay = () => {
    bookingActor.send({type: 'day.prev'});
  };

  const handleRescheduleNext = () => {
    // this extra event is to ensure that the selected time slot is confirmed before navigating to the next step in reschedule flow
    if (selectedTimeSlot) {
      bookingActor.send({type: 'timeslot.confirm', timeSlot: selectedTimeSlot});
    }
    bookingActor.send({type: 'rescheduleReasonForm.go'});
  };

  const handleBookingFormNext = () => {
    // this extra event is to ensure that the selected time slot is confirmed before navigating to the next step in booking form flow
    if (selectedTimeSlot) {
      bookingActor.send({type: 'timeslot.confirm', timeSlot: selectedTimeSlot});
    }
    bookingActor.send({type: 'bookingForm.go'});
  };

  const handleNoneOfTheseWorkForMe = () => {
    bookingActor.send({type: 'requestMoreAvailabilityForm.go'});
  };

  const canNavigateNext = currentSequenceNumber <= totalDays - 1;
  const canNavigatePrev = currentSequenceNumber > 0;
  const showNoneOfTheseWorkForMeButton =
    !canGoToeNextWeek && !isAvailabilityLoading && !isSequentialTemplate;

  return (
    <div className={css.navigationContainer}>
      <div className={css.actionButtons}>
        {canNavigatePrev && (
          <Button
            size="medium"
            type="tertiary"
            iconLeftName="chevron-left"
            onClick={handlePrevDay}
            disabled={isAvailabilityLoading}
            testId={generateTestId({
              base: TEST_ID.BOOKING_FLOW,
              slot: 'prev-day-button',
              index: currentSequenceNumber.toString(),
            })}
          >
            {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
            {isMultiDay
              ? `${t('Day', 'Day')} ${currentSequenceNumber}`
              : t('Back', 'Back')}
          </Button>
        )}

        {showNoneOfTheseWorkForMeButton && (
          <Button
            size="medium"
            type="tertiary"
            onClick={handleNoneOfTheseWorkForMe}
            disabled={isAvailabilityLoading || hasAvailabilityError}
          >
            {t('None_of_these_work_for_me', 'None of these work for me')}
          </Button>
        )}

        {canNavigateNext && (
          <BrandButton
            actionType="submit"
            size="medium"
            iconRightName="chevron-right"
            disabled={!selectedTimeSlot || isAvailabilityLoading}
            onClick={() => {
              if (currentSequenceNumber === totalDays - 1) {
                if (isRescheduleFlow) {
                  handleRescheduleNext();
                } else {
                  handleBookingFormNext();
                }
              } else {
                handleNextDay();
              }
            }}
            testId={generateTestId({
              base: TEST_ID.BOOKING_FLOW,
              slot: 'next-day-button',
              index: currentSequenceNumber.toString(),
            })}
          >
            {t('Next', 'Next')}
          </BrandButton>
        )}
      </div>
    </div>
  );
};
